import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

import {
  clientsResource,
  getDataHome,
  paymentsResource,
} from "../../helpers/configRoutes/backend_helper";

import CustomCalendarRange from "../../components/elements/CustomCalendarRange";
import CustomSelect from "../../components/form/CustomSelect";
import CustomButton from "../../components/form/CustomButton";
import { currencyUSD } from "../../helpers/forms/formater";

const Home = () => {
  const [operators, setOperators] = useState([]);
  const [trailer, setTrailer] = useState([]);
  const [trucks, setTrucks] = useState([]);
  
  // Aquí guardaremos la lista cruda de cargas (loads)
  const [dataLoads, setDataLoads] = useState([]);

  const date = new Date();

  const [filtersPayment, setFiltersPayment] = useState({
    month: date,
    home: true,
  });

  const [filters, setFilters] = useState({
    operator_id: "",
    trailer_id: "",
    truck_id: "",
    start_date: "",
    end_date: "",
    selectButtonRange: 29,
  });

  // Estado inicial de la gráfica
  const [chartData, setChartData] = useState([]);
  const [dataHome, setDataHome] = useState({});
  const [loadingHome, setLoadingHome] = useState(false);

  /* ======================
      HELPERS (PROCESAMIENTO DE DATOS)
  ====================== */
  
  // Función para transformar tus loads en datos para la gráfica
  const processLoadData = (loadsData) => {
    if (!Array.isArray(loadsData) || loadsData.length === 0) return [];

    // 1. Filtramos cargas válidas (con fecha y cobro) y Ordenamos por fecha
    const sortedLoads = [...loadsData]
      .filter((l) => l.pick_up_date && l.charges)
      .sort((a, b) => new Date(a.pick_up_date) - new Date(b.pick_up_date));

    // 2. Agrupamos los cobros por día (DD/MM)
    const grouped = sortedLoads.reduce((acc, curr) => {
      const dateObj = new Date(curr.pick_up_date);
      // Formato día/mes (ej: 28/01)
      const day = dateObj.getDate().toString().padStart(2, '0');
      const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
      const key = `${day}/${month}`;

      if (!acc[key]) acc[key] = 0;
      acc[key] += parseFloat(curr.charges); // Sumar charges
      return acc;
    }, {});

    // 3. Convertimos a array para Recharts: [{ name: "28/01", sales: 5700 }, ...]
    return Object.keys(grouped).map((key) => ({
      name: key,
      sales: grouped[key],
    }));
  };

  /* ======================
      EFFECTS
  ====================== */

  useEffect(() => {
    const getPayments = async () => {
      try {
        // NOTA: Asumimos que paymentsResource devuelve la lista de loads que me mostraste.
        // Si tu backend devuelve { items: [...] } ajusta aquí: response.items
        const response = await paymentsResource("get", {
          params: filtersPayment,
        });

        // Detectar si la respuesta es el array directo o viene dentro de una propiedad
        const rawLoads = Array.isArray(response) ? response : (response.items || response.data || []);
        
        setDataLoads(rawLoads);

        // Procesar los datos para la gráfica
        const processedChart = processLoadData(rawLoads);
        setChartData(processedChart);

      } catch (error) {
        console.error("Error fetching payment data:", error);
        setChartData([]); // Limpiar en caso de error
      }
    };

    getPayments();
  }, [filtersPayment]);

  useEffect(() => {
    const getClients = async () => {
      await clientsResource("get", { params: filters });
    };
    getClients();
  }, [filters]);

  useEffect(() => {
    const getHomeInfo = async () => {
      setLoadingHome(true);
      const response = await getDataHome();
      setDataHome(response || {});
      setChartData(response.chartData)
      setLoadingHome(false);
    };
    getHomeInfo();
  }, []);

  const renderMonthContent = (month, shortMonth, longMonth, day) => {
    const year = new Date(day).getFullYear();
    return <span title={`${longMonth} ${year}`}>{shortMonth}</span>;
  };

  /* ======================
      RENDER
  ====================== */

  return (
    <>
      {/* Header */}
      <h1 className="text-2xl font-bold text-cs-dark mb-6">
        Dashboard
      </h1>

      {/* ======================
          FILTERS
      ====================== */}
      <div className="bg-cs-surface border border-cs-border rounded-2xl p-5 mb-6 animate-fadeIn">
        <div className="flex justify-end mb-3">
          <CustomButton
            variant="outline"
            onClick={() =>
              setFilters({
                operator_id: "",
                trailer_id: "",
                truck_id: "",
                start_date: "",
                end_date: "",
                selectButtonRange: null,
              })
            }
            className="flex items-center gap-2 rounded-full"
          >
            <span className="material-symbols-outlined text-[16px]">
              filter_alt_off
            </span>
            Limpiar filtros
          </CustomButton>
        </div>

        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 w-full">
            <CustomSelect
              isClearable
              holder="Operators"
              options={operators}
              value={filters.operator_id}
              handleChange={(e) =>
                setFilters({ ...filters, operator_id: e.target.value })
              }
            />

            <CustomSelect
              isClearable
              holder="Trailers"
              options={trailer}
              value={filters.trailer_id}
              handleChange={(e) =>
                setFilters({ ...filters, trailer_id: e.target.value })
              }
            />

            <CustomSelect
              isClearable
              holder="Truck"
              options={trucks}
              value={filters.truck_id}
              handleChange={(e) =>
                setFilters({ ...filters, truck_id: e.target.value })
              }
            />

            <CustomCalendarRange
              isClearable
              holder="Date Range"
              valueStart={filters.start_date}
              valueEnd={filters.end_date}
              filters={filters}
              setFilters={setFilters}
            />
          </div>

          {/* Quick ranges */}
          <div className="flex gap-2 justify-end">
            {[
              { label: "Día", days: 0 },
              { label: "Semana", days: 6 },
              { label: "Mes", days: 29 },
            ].map(({ label, days }) => {
              const isActive = filters.selectButtonRange === days;

              return (
                <CustomButton
                  key={label}
                  variant={isActive ? "primary" : "outline"}
                  onClick={() => {
                    const end = new Date();
                    const start = new Date();
                    start.setDate(end.getDate() - days);

                    setFilters({
                      ...filters,
                      start_date: start,
                      end_date: end,
                      selectButtonRange: days,
                    });
                  }}
                  className={isActive ? "scale-[1.03] shadow-md" : ""}
                >
                  {label}
                </CustomButton>
              );
            })}
          </div>
        </div>
      </div>

      {/* ======================
          STATS
      ====================== */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          {
            label: "Total Clients",
            value: dataHome.clients || 0,
          },
          {
            label: "Active Loads",
            value: dataHome.loadsActives || 0,
          },
          {
            label: "Total Revenue",
            value: currencyUSD(dataHome.revenue || 0),
          },
          {
            label: "Total Expenses",
            value: currencyUSD(dataHome.expenses || 0),
          },
        ].map((item) => (
          <div
            key={item.label}
            className="
              bg-cs-bg
              border border-cs-border
              rounded-2xl p-5
              flex flex-col gap-2
              shadow-sm hover:shadow-md
              transition
            "
          >
            <span className="text-sm text-cs-muted">{item.label}</span>
            <span className="text-2xl font-bold text-cs-dark">
              {item.value}
            </span>
          </div>
        ))}
      </div>

      {/* ======================
          CHART
      ====================== */}
      <div className="bg-cs-bg border border-cs-border rounded-2xl p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <h2 className="text-lg font-semibold text-cs-dark">
            Sales Overview (Daily Revenue)
          </h2>

          <DatePicker
            selected={filtersPayment.month}
            onChange={(e) =>
              setFiltersPayment({ ...filtersPayment, month: e })
            }
            showMonthYearPicker
            dateFormat="MM/yyyy"
            renderMonthContent={renderMonthContent}
            className="
              px-3 py-2 rounded-lg
              border border-cs-border
              bg-cs-bg text-sm
              focus:outline-none focus:ring-2 focus:ring-cs-primary/40
            "
          />
        </div>

        <div className="w-full h-[300px]">
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                <XAxis 
                  dataKey="name" 
                  axisLine={false} 
                  tickLine={false} 
                  tick={{ fill: '#6B7280', fontSize: 12 }}
                  dy={10}
                />
                <YAxis 
                  axisLine={false} 
                  tickLine={false} 
                  tick={{ fill: '#6B7280', fontSize: 12 }}
                  tickFormatter={(value) => `$${value/1000}k`} 
                />
                <Tooltip 
                  formatter={(value) => [currencyUSD(value), "Revenue"]}
                  contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                />
                <Line
                  type="monotone"
                  dataKey="sales"
                  stroke="#FF7A00"
                  strokeWidth={3}
                  dot={{ r: 4, fill: '#FF7A00', strokeWidth: 2, stroke: '#fff' }}
                  activeDot={{ r: 7, fill: '#FF7A00', strokeWidth: 0 }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-full text-cs-muted text-sm">
              No data available for this period
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Home;